import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { useAppMode } from "@/context/AppModeContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SendOfferModal from "@/components/SendOfferModal";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import { toast } from "sonner";
import { Sparkles, MapPin, ExternalLink, ChevronDown, ChevronUp, Gem, Loader2, RefreshCw, Pencil } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

const CATEGORY_EMBLEMS = {
  dining: "🍽️",
  adventure: "🎢",
  culture: "🎭",
  nightlife: "🍸",
  romantic: "🌹",
};

const CATEGORY_TO_DATE_TYPE = {
  dining: "dinner",
  adventure: "activity",
  culture: "gallery",
  nightlife: "coffee",
  romantic: "custom",
};

export default function DateIdeasFeed() {
  const { isPayer, isDluxe } = useAppMode();
  const { t } = useLocale();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cityDialog, setCityDialog] = useState(false);
  const [cityInput, setCityInput] = useState("");
  const [pickerIdea, setPickerIdea] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [offerTarget, setOfferTarget] = useState(null);
  const [offerInitialData, setOfferInitialData] = useState(null);

  const fetchIdeas = useCallback(async ({ city, refresh } = {}) => {
    const params = new URLSearchParams();
    const c = city ?? localStorage.getItem("date_ideas_city") ?? "";
    if (c) params.set("city", c);
    if (refresh) params.set("refresh", "true");
    const r = await api.get(`/date-ideas${params.toString() ? `?${params}` : ""}`);
    setData(r.data);
    return r.data;
  }, []);

  useEffect(() => {
    fetchIdeas().catch(() => {});
  }, [fetchIdeas]);

  if (!data?.ideas?.length) return null;

  const airbnbUrl = `https://www.airbnb.com/s/${encodeURIComponent(data.city)}/experiences`;
  const mapsUrl = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await fetchIdeas({ refresh: true });
      setOpen(true);
      toast.success("Fresh date ideas served! ✨");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't refresh ideas right now");
    } finally {
      setRefreshing(false);
    }
  };

  const saveCity = async () => {
    const c = (cityInput || "").split(",")[0].trim();
    if (!c) return;
    localStorage.setItem("date_ideas_city", c);
    setCityDialog(false);
    setRefreshing(true);
    try {
      await fetchIdeas({ city: c });
      setOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load ideas for that city");
    } finally {
      setRefreshing(false);
    }
  };

  const openPicker = async (idea) => {
    setPickerIdea(idea);
    if (candidates !== null) return;
    setLoadingCandidates(true);
    try {
      const r = await api.get("/users/date-candidates");
      setCandidates(r.data.candidates || []);
    } catch {
      setCandidates([]);
    } finally {
      setLoadingCandidates(false);
    }
  };

  const pickMatch = (person) => {
    setOfferInitialData({
      date_type: CATEGORY_TO_DATE_TYPE[pickerIdea.category] || "custom",
      location: `${pickerIdea.title}, ${data.city}`,
      message: `I saw this date idea and thought of you: ${pickerIdea.title} — ${pickerIdea.description}`,
      experience_url: airbnbUrl,
    });
    setOfferTarget(person);
    setPickerIdea(null);
  };

  return (
    <div className="mb-8" data-testid="date-ideas-feed">
      <div className="w-full flex items-center justify-between mb-3 gap-2">
        <p className="font-semibold flex items-center gap-2 text-left flex-wrap">
          <Sparkles className="w-4 h-4 text-rose-400 shrink-0" /> {t("ideas.near")}{" "}
          <button
            type="button"
            onClick={() => {
              setCityInput(data.city);
              setCityDialog(true);
            }}
            className="inline-flex items-center gap-1 text-primary hover:underline"
            title="Change city"
            data-testid="date-ideas-city-btn"
          >
            {data.city} <Pencil className="w-3 h-3" />
          </button>
        </p>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-white/5 transition-colors disabled:opacity-60"
            title="Get 5 brand-new ideas"
            data-testid="date-ideas-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="p-2 rounded-lg text-muted-foreground hover:text-white hover:bg-white/5 transition-colors"
            data-testid="date-ideas-toggle"
          >
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {open && (
        <>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            {data.ideas.map((idea, i) => (
              <div
                key={i}
                className="min-w-[230px] max-w-[230px] p-4 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-colors flex flex-col"
                data-testid={`date-idea-${i}`}
              >
                <p className="text-2xl mb-2">{CATEGORY_EMBLEMS[idea.category] || "💜"}</p>
                <p className="font-semibold text-sm mb-1">{idea.title}</p>
                <p className="text-xs text-muted-foreground mb-3 flex-1">{idea.description}</p>
                <div className="flex items-center gap-4 text-xs">
                  <a
                    href={airbnbUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-rose-400 hover:underline"
                    data-testid={`idea-airbnb-${i}`}
                  >
                    <ExternalLink className="w-3 h-3" /> Airbnb
                  </a>
                  <a
                    href={mapsUrl(`${idea.title} ${data.city}`)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                    data-testid={`idea-maps-${i}`}
                  >
                    <MapPin className="w-3 h-3" /> {t("ideas.map")}
                  </a>
                </div>
                {isPayer && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="w-full mt-3 h-8 text-xs bg-primary/15 text-primary hover:bg-primary/25 border border-primary/25"
                    onClick={() => openPicker(idea)}
                    data-testid={`idea-use-btn-${i}`}
                  >
                    <Gem className="w-3.5 h-3.5 mr-1.5" /> {t("ideas.plan")}
                  </Button>
                )}
              </div>
            ))}
          </div>

          {data.popular_venues?.length > 0 && (
            <div className="mt-3" data-testid="popular-venues">
              <p className="text-xs font-medium text-muted-foreground mb-2">{t("ideas.popular")}</p>
              <div className="flex flex-wrap gap-2">
                {data.popular_venues.map((v, i) => (
                  <a
                    key={i}
                    href={mapsUrl(v.location)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                    data-testid={`popular-venue-${i}`}
                  >
                    <MapPin className="w-3 h-3 text-primary" />
                    <span className="max-w-[220px] truncate">{v.location}</span>
                    <span className="text-muted-foreground">· {v.dates} date{v.dates === 1 ? "" : "s"}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={cityDialog} onOpenChange={setCityDialog}>
        <DialogContent className="max-w-sm bg-card border-white/10" data-testid="date-ideas-city-dialog">
          <DialogHeader>
            <DialogTitle>{t("ideas.cityTitle")}</DialogTitle>
            <DialogDescription>
              {t("ideas.cityDesc")}
            </DialogDescription>
          </DialogHeader>
          <LocationAutocomplete
            id="date-ideas-city"
            placeholder="Search a city..."
            value={cityInput}
            onChange={setCityInput}
          />
          <div className="flex gap-2 justify-end mt-1">
            <Button variant="outline" onClick={() => setCityDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveCity}
              disabled={!(cityInput || "").trim()}
              data-testid="save-city-btn"
            >
              {t("ideas.show")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pickerIdea} onOpenChange={(o) => !o && setPickerIdea(null)}>
        <DialogContent className="max-w-sm bg-card border-white/10 max-h-[80vh] overflow-y-auto" data-testid="idea-match-picker">
          <DialogHeader>
            <DialogTitle>{t("ideas.whoFor")}</DialogTitle>
            <DialogDescription>
              "{pickerIdea?.title}" — {t("ideas.pickDesc")}
            </DialogDescription>
          </DialogHeader>
          {loadingCandidates ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : candidates?.length ? (
            <div className="space-y-2">
              {candidates.map((p) => (
                <button
                  key={p.user_id}
                  type="button"
                  onClick={() => pickMatch(p)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-left transition-colors"
                  data-testid={`idea-pick-${p.user_id}`}
                >
                  <img
                    src={p.photos?.[0] || p.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name || "User")}&background=b91c1c&color=fff&size=100`}
                    alt={p.name}
                    className="w-10 h-10 rounded-full object-cover shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">
                      {p.name}
                      {p.age ? `, ${p.age}` : ""}
                    </p>
                    {p.location && <p className="text-xs text-muted-foreground truncate">{p.location}</p>}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center" data-testid="idea-picker-empty">
              {t("ideas.emptyPrefix")}{" "}
              <Link to="/discover" className="text-primary hover:underline" onClick={() => setPickerIdea(null)}>
                Discover
              </Link>{" "}
              {t("ideas.emptySuffix")}
            </p>
          )}
        </DialogContent>
      </Dialog>

      <SendOfferModal
        isOpen={!!offerTarget}
        onClose={() => setOfferTarget(null)}
        woman={offerTarget}
        initialData={offerInitialData}
      />
    </div>
  );
}
